/**
 * The single definition of the verdict color vocabulary, imported by RoutesPanel.tsx,
 * BusOpsShell.tsx's RouteDetail, and MbmtReportsShell.tsx (which used to define its own
 * private copy -- now imports intelVerdictColor from here instead, so there is exactly
 * one source of truth, not two that happen to agree today). Exact values: ok emerald,
 * tricky amber, short rose, hazardous rose-700 (bold); no `unknown` key -- falls through
 * to slate.
 */
export const INTEL_VERDICT_COLOR: Record<string, string> = {
  ok: 'text-emerald-600',
  tricky: 'text-amber-600',
  short: 'text-rose-600',
  hazardous: 'text-rose-700 font-semibold',
}

export const INTEL_VERDICT_FALLBACK_COLOR = 'text-slate-500'

/** The five known verdict values. `worstVerdict()` never returns anything outside this
 * set -- any unrecognised wire value normalizes to 'unknown'. */
export type VerdictKey = 'ok' | 'short' | 'tricky' | 'hazardous' | 'unknown'

export function intelVerdictColor(verdict: string | null | undefined): string {
  return INTEL_VERDICT_COLOR[verdict ?? ''] ?? INTEL_VERDICT_FALLBACK_COLOR
}

/** Background fill for a verdict shown as a badge (as opposed to a `bg-current` dot,
 * which derives its own fill from the text colour above). Kept separate from
 * INTEL_VERDICT_COLOR so the dot's `bg-current` trick isn't clobbered by an explicit
 * background. */
const INTEL_VERDICT_BADGE_BG: Record<string, string> = {
  ok: 'bg-emerald-50',
  tricky: 'bg-amber-50',
  short: 'bg-rose-50',
  hazardous: 'bg-rose-100',
}

const INTEL_VERDICT_BADGE_FALLBACK_BG = 'bg-slate-100'

/** A verdict badge's full class list (text colour + background) so it reads as a
 * badge, not bare colored text. */
export function intelVerdictBadgeClass(verdict: string | null | undefined): string {
  const bg = INTEL_VERDICT_BADGE_BG[verdict ?? ''] ?? INTEL_VERDICT_BADGE_FALLBACK_BG
  return `${intelVerdictColor(verdict)} ${bg}`
}

/** Worst-first rank: hazardous > short > tricky > unknown > ok. `unknown` ("no
 * timetable data yet") ranks worse than `ok` ("measured and fine") but must never
 * visually outrank a confirmed `short`/`hazardous` finding. */
export const INTEL_VERDICT_RANK: Record<string, number> = {
  hazardous: 0, short: 1, tricky: 2, unknown: 3, ok: 4,
}

/** Any value not a recognised key in INTEL_VERDICT_RANK -- including null/undefined,
 * empty/whitespace strings, wrong casing, or a stale congestion_bucket value like
 * "jammed" -- normalizes to 'unknown' rather than passing through untyped-but-
 * unchecked. This is the single point where wire junk becomes a safe, known verdict. */
export function normalizeVerdict(verdict: string | null | undefined): VerdictKey {
  const key = typeof verdict === 'string' ? verdict : ''
  // Object.hasOwn (not `key in INTEL_VERDICT_RANK`): the `in` operator also matches
  // inherited Object.prototype members (`toString`, `hasOwnProperty`, `constructor`,
  // ...), which a wire value could easily collide with.
  return Object.hasOwn(INTEL_VERDICT_RANK, key) ? (key as VerdictKey) : 'unknown'
}

/** The worst verdict across a set of rows (e.g. one route's own intel rows). Empty
 * input -- a route with no intel rows at all -- reads as 'unknown', the same bucket as
 * "have only unknown-verdict rows": both mean "nothing to judge yet" to an ops
 * scanner. Output is always one of the five known verdicts -- never a passthrough of
 * whatever string the wire happened to send. */
export function worstVerdict(verdicts: (string | null | undefined)[]): VerdictKey {
  if (verdicts.length === 0) return 'unknown'
  let worst = normalizeVerdict(verdicts[0])
  let worstRank = INTEL_VERDICT_RANK[worst] as number
  for (const v of verdicts) {
    const norm = normalizeVerdict(v)
    const rank = INTEL_VERDICT_RANK[norm] as number
    if (rank < worstRank) {
      worst = norm
      worstRank = rank
    }
  }
  return worst
}

/** Only a confirmed problem floats a route to the top of the live list -- `unknown`
 * ("no timetable data") is not a risk signal on its own and must not jump the queue
 * ahead of a measured-fine `ok` route the way `short`/`hazardous` do. */
export function isRiskyVerdict(verdict: string | null | undefined): boolean {
  return verdict === 'hazardous' || verdict === 'short'
}
